import { cn } from '@/shared/lib/cn';
import { shapeBubbleAmp } from '../lib/waveform-style';

interface Props {
  /** Array de 32 floats (0..1). Quando null/vazio, renderiza padrão neutro. */
  amplitudes?: number[] | null | undefined;
  /** Progresso atual da reprodução (0..1) — colore as barras à esquerda. */
  progress?: number;
  /** Callback ao clicar numa posição da waveform (0..1). */
  onSeek?: (ratio: number) => void;
  className?: string;
}

const TARGET_BARS = 32;

// Padrão neutro pra Cloud API (sem waveform real). Sequência fixa estética.
const NEUTRAL_PATTERN = [
  0.3, 0.5, 0.4, 0.7, 0.5, 0.3, 0.6, 0.8, 0.5, 0.4, 0.6, 0.5, 0.3, 0.5, 0.7, 0.4, 0.6, 0.5, 0.4,
  0.6, 0.8, 0.5, 0.3, 0.5, 0.6, 0.4, 0.7, 0.5, 0.3, 0.5, 0.4, 0.3,
];

/**
 * Render instantâneo de waveform usando barrinhas estáticas (zero decode no
 * cliente). Igual `app-waveform-bars` do AtenderBem. WaveSurfer continua
 * disponível como upgrade pós-play pra scrubbing fluido.
 */
export function WaveformBars({ amplitudes, progress = 0, onSeek, className }: Props) {
  const bars = normalize(amplitudes ?? NEUTRAL_PATTERN);
  const interactive = Boolean(onSeek);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(ratio);
  };

  return (
    <div
      onClick={handleClick}
      role={interactive ? 'slider' : undefined}
      aria-valuenow={interactive ? Math.round(progress * 100) : undefined}
      aria-valuemin={interactive ? 0 : undefined}
      aria-valuemax={interactive ? 100 : undefined}
      className={cn(
        'flex h-8 flex-1 items-center gap-[2px]',
        interactive && 'cursor-pointer',
        className,
      )}
    >
      {bars.map((h, i) => {
        const barProgress = (i + 0.5) / TARGET_BARS;
        const played = barProgress <= progress;
        return (
          <div
            key={i}
            className={cn('w-[2px] flex-1 rounded-sm', played ? 'bg-brand-500' : 'bg-n-400')}
            // Floor de 8% pra nenhuma barra sumir; altura por ganho fixo (volume real).
            style={{ height: `${Math.max(8, shapeBubbleAmp(h) * 100)}%` }}
          />
        );
      })}
    </div>
  );
}

function normalize(input: number[]): number[] {
  if (input.length === TARGET_BARS) return input;
  // Resample pra 32 mantendo proporções.
  const out: number[] = [];
  for (let i = 0; i < TARGET_BARS; i++) {
    const idx = Math.floor((i * input.length) / TARGET_BARS);
    out.push(input[idx] ?? 0);
  }
  return out;
}
